import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import GeneralDialogLayout from '@Containers/Dialogs/GeneralDialogLayout/GeneralDialogLayout';
import Button from '@Components/Button';
import ExportFormats from '@Pages/RetroSynthesis/ExportRoute/components/ExportFormats';
import ExportDisclaimer from '@Pages/RetroSynthesis/ExportRoute/components/ExportDisclaimer';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import ExportOptions, { EXPORT_FORM_DESCRIPTION_OPTION } from '@Pages/RetroSynthesis/ExportRoute/components/ExportOptions';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import DialogWithNotificationInfoLayout from '@Containers/Dialogs/DialogsWithIcon/DialogWithInfoLayout';
import ExportSelection, { ExportRTSelections } from '@Pages/RetroSynthesis/ExportRoute/components/ExportSelection';
import { isAnyPredictiveRetrosynthesisAvailable } from '../predictiveUtils';

import './ExportRouteDialog.scss';

export const ExportTab = Object.freeze({
  REACTIONS: 0,
});

// Extracted helper function
function handleExport({ onExport, currentTabIndex, rtSelection, selectedFormatOption, additionalOptions, exportFormDescription }) {
  onExport({
    currentTabIndex,
    rtSelection,
    selectedFormat: selectedFormatOption,
    additionalOptions: additionalOptions?.filter((o) => o.checked && o.name !== EXPORT_FORM_DESCRIPTION_OPTION.name).map((o) => o.name),
    headline:
      selectedFormatOption?.headline && additionalOptions?.filter((o) => o.name === EXPORT_FORM_DESCRIPTION_OPTION.name)[0]?.checked
        ? exportFormDescription
        : '',
  });
}

/* eslint-disable react-hooks/exhaustive-deps */
function ExportRouteDialog({ onExport, onClose, tabs, isLoading, isBusy, disableSelectionOptions, ...rest }) {
  const loadingState = isLoading && !tabs;
  const enableExportSelections = isAnyPredictiveRetrosynthesisAvailable();
  const [currentTabIndex, setCurrentTabIndex] = useState(ExportTab.REACTIONS);
  const [selectedFormatOption, setSelectedFormatOption] = useState();
  const [additionalOptions, setAdditionalOptions] = useState();
  const [rtSelection, setRtSelection] = useState(
    !enableExportSelections || disableSelectionOptions ? ExportRTSelections.ALL : ExportRTSelections.SELECTED_ONLY
  );
  const [exportFormDescription, setExportFormDescription] = useState('');

  const currentTab = useMemo(() => (tabs?.length >= currentTabIndex ? tabs[currentTabIndex] : {}), [tabs, currentTabIndex]);

  const isReactionExportAsRDFileEnabled = Auth.isFeatureEnabled(FEATURES.enableReactionExportAsRDFile);
  const excludedFormats = useMemo(
    () => (isReactionExportAsRDFileEnabled || currentTabIndex !== ExportTab.REACTIONS ? [] : ['RDFILE']),
    [isReactionExportAsRDFileEnabled, currentTabIndex]
  );

  const exportFormatOptions = useMemo(() => {
    if (!currentTab || !currentTab.formats) {
      return [];
    }
    return currentTab.formats.filter((format) => !excludedFormats.includes(format.value));
  }, [currentTab, excludedFormats]);

  useEffect(() => {
    if (tabs) {
      setSelectedFormatOption(null);
    }
  }, [currentTabIndex]);
  useEffect(() => {
    if (selectedFormatOption) {
      const options = selectedFormatOption.additional_options;
      if (selectedFormatOption.headline && options.indexOf(EXPORT_FORM_DESCRIPTION_OPTION) < 0) {
        options.push(EXPORT_FORM_DESCRIPTION_OPTION);
      }
      setAdditionalOptions(options);
    } else {
      setAdditionalOptions([]);
    }
    setExportFormDescription('');
  }, [selectedFormatOption]);

  return isBusy ? (
    <DialogWithNotificationInfoLayout size="md" character="!" layoutType="info" header="Export" selfClose={onClose}>
      Please cancel the previous Export job before you start a new one.
    </DialogWithNotificationInfoLayout>
  ) : (
    <GeneralDialogLayout
      {...rest}
      size="sm"
      movable={false}
      className="react-dialogs-scrollable export-route-popup"
      header={
        <h4>
          <ul className="header-menu-items new-main-site-navigation-list-items">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
            <li
              className={cn('new-menu-link', { 'new-menu-link-active': !currentTabIndex })}
              onClick={() => setCurrentTabIndex(ExportTab.REACTIONS)}>
              Export reactions
            </li>
          </ul>
        </h4>
      }
      footer={
        <div className="els-btn-group-right">
          <Button
            disabled={!selectedFormatOption?.value}
            buttonConfig={{
              type: 'primary',
              size: 'sm',
              label: 'Export',
              e2eName: 'export-dialog-export',
            }}
            iconConfig={{
              name: 'chevron-right',
              size: 'sm',
            }}
            onClick={() =>
              handleExport({
                onExport,
                currentTabIndex,
                rtSelection,
                selectedFormatOption,
                additionalOptions,
                exportFormDescription,
              })
            }
          />
        </div>
      }
      selfClose={onClose}>
      <div className="export-route-popup__container">
        {loadingState ? (
          <SimpleSpinner />
        ) : (
          <>
            <div className="export-route-popup__container_content">
              <ExportFormats
                options={exportFormatOptions}
                value={selectedFormatOption}
                tooltipText={currentTab && currentTab.formatsTooltip}
                onSelectFormat={setSelectedFormatOption}
              />
              {enableExportSelections && (
                <ExportSelection setSelection={setRtSelection} selection={rtSelection} disableSelectedOnly={disableSelectionOptions} />
              )}
              {additionalOptions?.length > 0 && (
                <ExportOptions
                  additionalOptions={additionalOptions}
                  setSelectedOptions={setAdditionalOptions}
                  exportFormDescription={exportFormDescription}
                  setExportFormDescription={setExportFormDescription}
                />
              )}
            </div>
            <ExportDisclaimer />
          </>
        )}
      </div>
    </GeneralDialogLayout>
  );
}

ExportRouteDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  tabs: PropTypes.array,
  isBusy: PropTypes.bool,
  isLoading: PropTypes.bool,
  disableSelectionOptions: PropTypes.bool,
};

export default ExportRouteDialog;
